<script lang="ts">
  // BJL-CHECK: Button (panel actions), SwitchBare (onboard 3D camera toggle).
  //
  // Riding panel: bound to a vehicle.
  //
  // Riding panel displays subway rollsign or bus LCD view for boarded vehicle.
  import { untrack } from 'svelte'
  import { Button } from '@byjackli-labs/ui/Button'
  import { SwitchBare } from '@byjackli-labs/ui/Switch'
  import { Colors, getRouteColor } from '../../ui/colors'

  import { LED_AMBER, LED_FONT, LED_GREEN, LED_RAIL, LED_RED, ledGlow, Type } from '../../ui/theme'
  import { alightHeadline, hasReachedDestination, useRiderStore } from '../../store/rider'
  import { useVehicleWatchStore } from '../../store/vehicleWatch'
  import { storeSelect, storeState } from '../../lib/store.svelte'
  import {
    panelShell,
    PANEL_BOTTOM_GUTTER,
    PANEL_TOP_RAIL_H,
    fmtCountdown,
    fmtShort,
    fmtTime,
    nearestSnap,
    signHeadline,
    signSkin,
    SNAP_MINI,
    SNAP_DEFAULT,
    SNAP_DEFAULT_BUS,
    SNAP_FULL,
    STRIP_PAD_X,
  } from './shell'
  import RouteBadge from '../RouteBadge.svelte'
  import BusStopScreen from './BusStopScreen.svelte'
  import TrainStopScreen from './TrainStopScreen.svelte'
  import BoardedCrowdingReport from './BoardedCrowdingReport.svelte'

  interface Props {
    startExpanded?: boolean
  }

  const { startExpanded }: Props = $props()

  // The control row's two economies, shared by both faces so neither can drift from the
  // other. The row is no longer the tightest thing in the panel — the ✕ moved to the route
  // line and took ~53px with it — but the gap stays shared, because what the three rows have
  // to keep is the same ORDER and the same corner, and a different gap on one face reads as a
  // different control cluster.
  //
  // `RAIL_ICON_BTN` moved WITH the ✕ (see `closeButton`) and is still what that button wears:
  // the bjl icon button's own box is ~53px, of which ~20 is padding around one glyph, and the
  // route line it now sits on is the tightest of the three places it has been. On the mini
  // face that row also carries the bullet, the next stop and the 3D toggle in one viewport
  // width; on the bus, every px of it is a px `signHeadline` does not get.
  const RAIL_LABEL_3D = '3D'
  const RAIL_GAP = 5
  const RAIL_ICON_BTN =
    'font-family: var(--bjl-font-body); min-width: 28px; padding: 0 3px;'
  /**
   * "3D" is what the row has width for, but it is a thin accessible name for a control that
   * moves the camera onto the vehicle. `SwitchBare` takes no `aria-label`, so the rest of the
   * phrase rides in the `<label>` where the name comes from, clipped out of the layout. The
   * visible text is a prefix of the whole, which is what WCAG's Label-in-Name asks for.
   *
   * Takes the face it is rendered on, because the noun is the only subway-specific thing left
   * in this control: the chase camera has always followed buses — `vehicleWatch` keys on a
   * vehicle id and nothing else, and the map's watched-vehicle branch handles `type === 'bus'`
   * explicitly. It was only ever the riding panel that withheld it.
   *
   * This toggle is now the app's ONLY way into the chase camera: the map's vehicle sheet
   * carried a "Watch in 3D" button until it was removed, so watch mode is reached by boarding
   * and nothing else. Take a control off any of the three faces and a rider loses it entirely.
   */
  const rail3dRest = (bus: boolean) => `: watch this ${bus ? 'bus' : 'train'} in 3D`
  const SR_ONLY =
    'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;'
    + ' overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0;'

  const rider = storeState(useRiderStore)
  const watchedVehicleId = storeSelect(useVehicleWatchStore, (s) => s.watchedVehicleId)

  // The two faces have different default heights (see `SNAP_DEFAULT_BUS`). The panel only
  // mounts while bound to a vehicle, so the initial read is already the right one — but it
  // must stay DERIVED, not captured: a planned trip's bus leg followed by a train leg calls
  // `startRide` again WITHOUT remounting this component, and a captured value leaves the
  // train wearing the bus's 455 with 115px of black under the sign.
  const snapDefault = $derived(
    rider.value.trackedVehicle?.type === 'bus' ? SNAP_DEFAULT_BUS : SNAP_DEFAULT,
  )

  // `untrack` because capturing the initial value IS the intent here — these three are the
  // drawer's own mutable height state, and the effect below is what keeps them in step.
  let height = $state(untrack(() => snapDefault))
  let dragging = $state(false)
  let heightBeforeWatch = untrack(() => snapDefault)
  let wasWatchingIn3d = false
  let lastSnapDefault = untrack(() => snapDefault)



  function onHandleDown(e: PointerEvent) {
    e.preventDefault()
    const startY = e.clientY
    const startH = height

    // The drawer rises from the bottom, so its free edge is the TOP one: pulling UP grows it.
    // Same sign as every other sheet in the app now (see `App`'s `onHandleDown`) — it read
    // the opposite one for as long as this hung from the top.
    const onMove = (ev: PointerEvent) => {
      height = Math.max(SNAP_MINI, Math.min(SNAP_FULL, startH - (ev.clientY - startY)))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      dragging = false
      height = nearestSnap(height, snapDefault)
    }

    dragging = true
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp, { once: true })
  }

  const trackedVehicle = $derived(rider.value.trackedVehicle)
  const watchingIn3d = $derived(
    trackedVehicle != null && watchedVehicleId.value === trackedVehicle.vehicleId,
  )
  const upcomingStops = $derived(rider.value.upcomingStops)
  const currentStopIndex = $derived(rider.value.currentStopIndex)
  const destinationStopId = $derived(rider.value.destinationStopId)
  const stopsUntilDestination = $derived(rider.value.stopsUntilDestination)
  const wrongVehicle = $derived(rider.value.wrongVehicle)

  // On a planned trip ✕ means "I got off this vehicle", not "abandon my trip": the leg stays
  // armed for the next train of the same line. Ending the trip is a separate control.
  const onPlan = $derived(rider.value.plannedLegIndex !== null)

  const routeColor = $derived(trackedVehicle ? getRouteColor(trackedVehicle.routeId) : '')
  const isMini = $derived(height <= SNAP_MINI + 10)

  // Vehicle display skin derivation.
  const isBus = $derived(trackedVehicle?.type === 'bus')
  const sign = $derived(signSkin(trackedVehicle?.type))

  // Upcoming remaining stops.
  const remaining = $derived(upcomingStops.slice(currentStopIndex + 1))
  const nextStop = $derived(remaining[0] ?? null)
  const isArriving = $derived(
    trackedVehicle?.status === 'INCOMING_AT' ||
      (nextStop?.arrivalTime != null &&
        (nextStop.arrivalTime > 1e11 ? nextStop.arrivalTime - Date.now() : nextStop.arrivalTime * 1000 - Date.now()) <= 60000),
  )

  // Where the sign says the train is going: the last stop it still calls at.
  const terminus = $derived(
    upcomingStops.length > 0 ? upcomingStops[upcomingStops.length - 1].stopName : null,
  )

  /** Measured headline width for dynamic font sizing. */
  let headlineW = $state(0)
  // The destination shrinks, then loses its cross street, rather than being ellipsised
  // mid-word — see `signHeadline`. The rollsign's letter-spacing is a fixed 2px and the LCD's
  // is 0.02em, so each face hands its own tracking to the fitter.
  const fitted = $derived(
    terminus
      ? signHeadline(terminus, headlineW, {
          font: sign.font,
          weight: 700,
          maxPx: isBus ? 22 : 28,
          minPx: isBus ? 15 : 19,
          trackingEm: isBus ? 0.02 : 0,
          trackingPx: isBus ? 0 : 2,
        })
      : null,
  )
  const headlineText = $derived(fitted?.text ?? `RIDING ${trackedVehicle?.routeId ?? ''}`)
  const headlinePx = $derived(fitted?.px ?? (isBus ? 22 : 28))
  const upcomingTransfer = $derived(rider.value.nextLeg())
  const walk = $derived(rider.value.transferWalk())

  const atDestination = $derived(hasReachedDestination(rider.value))
  const headline = $derived(alightHeadline(rider.value))

  // The one line with a deadline, for the train's destination panel: the wrong-vehicle
  // warning outranks the countdown, since a rider on the wrong train has nothing to count.
  // Worded shorter than the bus face's version — this one is set in a ~90px column.
  const notice = $derived(
    wrongVehicle ? "WRONG TRAIN — TAP A STOP TO GET OFF" : headline,
  )
  const noticeUrgent = $derived(wrongVehicle || atDestination || stopsUntilDestination === 1)

  const roomForExtras = $derived(height > snapDefault + 60)

  // Follow the new face's default when the vehicle type changes mid-trip — but only if the
  // drawer is still sitting on the old one. A rider who dragged to mini or full chose that
  // height, and a transfer is not a reason to overrule them. Guarded on `lastSnapDefault`
  // (a plain let, not state) so the write to `height` cannot re-trigger this effect.
  //
  // `heightBeforeWatch` follows by the SAME rule, and must: while the 3D camera is on, the
  // drawer is parked at SNAP_MINI and the height the rider will get back is held there
  // instead. Leave it behind and a transfer restores the previous face's default onto the new
  // one — a bus leg watched in 3D, then a train leg, and exiting the camera hands the train
  // the bus's 455 with 115px of black under the sign, which is the exact failure `height`'s
  // own guard above exists to prevent. It only became reachable in that direction when the
  // toggle reached the bus face; train→bus could already do the milder opposite.
  $effect(() => {
    const next = snapDefault
    if (next === lastSnapDefault) return
    if (height === lastSnapDefault) height = next
    if (heightBeforeWatch === lastSnapDefault) heightBeforeWatch = next
    lastSnapDefault = next
  })

  // The chase camera needs map space, so entering it folds the sign to its mini face. The
  // toggle remains in that face, making it reversible; Back/Escape exits are reflected here
  // too and restore the height the rider was using before the switch.
  $effect(() => {
    const watching = watchingIn3d
    if (watching === wasWatchingIn3d) return

    wasWatchingIn3d = watching
    if (watching) {
      heightBeforeWatch = height
      height = SNAP_MINI
    } else {
      height = heightBeforeWatch
    }
  })

  function setWatchIn3d(enabled: boolean) {
    const vehicleId = trackedVehicle?.vehicleId
    if (enabled && vehicleId) {
      useVehicleWatchStore.getState().watchVehicle(vehicleId)
    } else if (watchedVehicleId.value === vehicleId) {
      useVehicleWatchStore.getState().stopWatching()
    }
  }
</script>

<!-- The way out of the ride, and the ONE copy of it: every face renders this snippet, so the
     glyph, the box and — the part that matters — the accessible name cannot drift between
     them the way three inline buttons did.
     It rides the ROUTE LINE on all three faces: the sign band on the train (through
     `DestinationSign`'s `trailing`), the headline row on the bus, the single row on the mini
     face. That is the line every face has, so the target barely moves as the drawer changes
     height, and it is the line a rider is already looking at — where this line is, ✕ is at
     the end of it. It also costs no height anywhere: the bullet beside it is 30px on the
     train band and 26 on the mini row, against the button's own 28.
     ⚠️ It is NOT in any of the three control rows and must not go back into one. That is
     where it used to be, and it is the only control those rows held whose target moved when
     the drawer changed — bottom-right on the train, inside `BusStopScreen`'s footer on the
     bus, up in the mini row when collapsed. The rows keep crowding and the 3D toggle. -->
{#snippet closeButton()}
  <Button
    variant="text"
    size="sm"
    iconOnly
    onclick={() => rider.value.leaveVehicle()}
    aria-label={onPlan ? 'Get off this vehicle' : 'End ride'}
    style={RAIL_ICON_BTN}
  >✕</Button>
{/snippet}

<!-- The two rows that only appear once the rider has dragged past DEFAULT. Shared by both
     faces: the split has no room for them and the bus list would rather have the height. -->
{#snippet extras()}
  {#if upcomingTransfer && roomForExtras}
    <div
      style="
        flex-shrink: 0; display: flex; align-items: center; gap: 10px;
        border-top: 1px solid rgba(255,255,255,0.1);
        margin: 12px 20px 0; padding-top: 12px;
        font-family: var(--bjl-font-body);
      "
    >
      <span style="color: {Colors.textMuted}; {Type.eyebrow};">THEN</span>
      {#if walk}
        <span style="color: {Colors.textSecondary}; {Type.body};">🚶 {walk.minutes} min</span>
      {/if}
      <RouteBadge routeId={upcomingTransfer.routeId} size={22} />
      <span
        style="
          flex: 1; min-width: 0; color: {Colors.textSecondary}; {Type.body};
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        "
      >
        {upcomingTransfer.headsign
          ? `toward ${upcomingTransfer.headsign}`
          : `to ${upcomingTransfer.to.name}`}
      </span>
    </div>
  {/if}

  {#if onPlan && roomForExtras}
    <div
      style="
        flex-shrink: 0; display: flex; justify-content: flex-end;
        padding: 10px 20px 0;
        font-family: var(--bjl-font-body);
      "
    >
      <Button variant="secondary" size="sm" onclick={() => rider.value.endRide()}>
        End trip
      </Button>
    </div>
  {/if}
{/snippet}



{#if trackedVehicle}
  <!-- `height` is the sign's own geometry (see SNAP_DEFAULT) and nothing else: there is no
       safe-area inset to pay for at this edge, because the tab bar this drawer stands on has
       already paid it. It used to be `calc(height + safeTop())` with matching top padding,
       which is what kept a notch off the sign while the drawer hung from the top.
       `PANEL_BOTTOM_GUTTER` is inside the height (border-box) and SNAP_DEFAULT already carries
       it — it is the air the handle used to leave under the control row from down there. -->
  <div
    style="
      {panelShell}
      box-sizing: border-box;
      height: {height}px;
      padding-bottom: {PANEL_BOTTOM_GUTTER}px;
      display: flex; flex-direction: column;
      overflow: hidden;
      font-family: {sign.font};
      transition: {dragging ? 'none' : 'height 0.25s cubic-bezier(0.4,0,0.2,1)'};
    "
  >

    {#if sign.wash}
      <div
        aria-hidden="true"
        style="position: absolute; inset: 0; {sign.wash} pointer-events: none; z-index: 2;"
      ></div>
    {/if}

    <!-- Drag handle, on the drawer's free edge — the TOP one now that the drawer rises from
         the bottom. It is the drawer's first child for that reason, and it is where the tab
         sheets keep theirs, so the two read the same.
         Nothing else is in this rail. The ✕ spent a version here and came back down to the
         route line: this band is the one part of the drawer that says nothing, so a control
         in it reads as chrome on the drawer rather than part of the ride, and it sat a full
         row above the line the rider is actually reading. See `closeButton`. -->
    <div
      onpointerdown={onHandleDown}
      role="separator"
      aria-label="Resize ride panel"
      style="
        flex-shrink: 0; height: {PANEL_TOP_RAIL_H}px;
        display: flex; align-items: center; justify-content: center;
        cursor: ns-resize; user-select: none;
        touch-action: none;
      "
    >
      <div style="width: 36px; height: 4px; border-radius: 2px; background-color: rgba(255,255,255,0.25);"></div>
    </div>

    <!-- The face, in its own box so the handle above can never be pushed off the drawer. The
         sign is worth more than the snap height at SNAP_DEFAULT once an alight countdown is
         showing (which is what the hint's bottom gutter has always paid for), and a clipped
         handle would leave no way to drag back. That is now the WHOLE story: with the ▲/▼
         buttons gone the handle is the only height control the rider has, so this box is
         what keeps the drawer from stranding itself at whatever height it is on. -->
    <div style="flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden;">
    {#if isMini}
      <!-- Mini panel view: one row, centred in what the handle leaves it. -->
      <div style="flex: 1; min-height: 0; display: flex; align-items: center; gap: 10px; padding: 0 {STRIP_PAD_X}px;">
        <RouteBadge routeId={trackedVehicle.routeId} size={26} />
        <span
          style="
            flex: 1; min-width: 0; font-size: 22px; letter-spacing: {isBus ? '0.02em' : '1px'};
            {sign.ink(headline ? sign.urgent : sign.head, 8)}
            {isBus ? 'font-weight: 700;' : ''}
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          "
        >
          {headline ? `${headline}${nextStop ? ` · NEXT: ${nextStop.stopName}` : ''}` : (nextStop ? `NEXT: ${nextStop.stopName}` : `RIDING ${trackedVehicle.routeId}`)}
        </span>

        <!-- Same cluster as the expanded face's control row, same order, same corner — see
             the comment there. Both read `RAIL_LABEL_3D`, so the wording cannot drift.
             Unconditional: the mini face is ONE row shared by both vehicles, so the `!isBus`
             gate that used to sit here was the whole reason a bus rider had no way into the
             chase camera from the panel. Nothing behind the toggle was ever subway-only.
             The ✕ closes the row, as it does on the other two faces — and this row IS the
             mini face's route line, so it is the same line and the same end. -->
        <label
          for="ride-watch-3d-mini"
          style="font-family: var(--bjl-font-body); color: {Colors.textSecondary}; {Type.caption}; cursor: pointer; white-space: nowrap;"
        >{RAIL_LABEL_3D}<span style={SR_ONLY}>{rail3dRest(isBus)}</span></label>
        <SwitchBare
          id="ride-watch-3d-mini"
          size="sm"
          checked={watchingIn3d}
          onchange={(event) => setWatchIn3d(event.detail.checked)}
        />

        {@render closeButton()}
      </div>
    {:else if !isBus}
      <!-- The R160 car front: the sign band over the full-width rollsign strip. Nothing is
           stacked above it — the route bullet, where the train is going and the alight
           countdown are all IN the band, which is what SNAP_DEFAULT buys over the 455 the
           stacked version needed (see its own comment for where each px of it goes). -->
      <TrainStopScreen
        stops={remaining}
        {destinationStopId}
        {isArriving}
        routeId={trackedVehicle.routeId}
        {terminus}
        {notice}
        {noticeUrgent}
        onPick={(stopId) => rider.value.setDestination(stopId)}
        signTrailing={closeButton}
      />

      {@render extras()}

      <!-- ONE control row: crowding, then the 3D toggle — the same pair the mini face
           carries, in the same order and the same corner. The faces are one drawer at two
           heights, and a rider who collapses and re-opens it should not have to find a
           control again on the other side. Keep them in step: a control added to one belongs
           in ALL of them — the mini row, this row, and `BusStopScreen`'s footer, which is
           where that face keeps the same pair. `RAIL_LABEL_3D` and `rail3dRest` are shared by
           all three, so neither the wording nor the accessible name can drift between them.
           ⚠️ The ✕ is NOT one of them and must not come back into any of the three: it is on
           the ROUTE line — here, that is the sign band above the strip (see `closeButton`).
           There is no ▲/▼ here either: the drag handle at the drawer's top edge is the
           one way to change its height, on every face. The 3D toggle still moves it on its
           own (see the `watchingIn3d` effect), and that is now the only programmatic mover.
           This row used to be the tight one and is not any more. On a 360px phone
           `STRIP_PAD_X` leaves it 328px; the parts now come to ~255 — crowding ~196, the
           toggle 54, two gaps — where they came to ~323 with the ✕ and ~356 with the ▲ before
           that. All of the slack goes to crowding, the row's only flexible block, and none of
           it to a third control; that is what keeps the three faces the same shape. The
           toggle still reads "3D" with the rest of the phrase clipped into its label, because
           what the label is worth is measured against the mini row, which is tighter.
           The crowding block is the only flexible one (`flex: 1; min-width: 0`), so a
           narrower viewport than this shortens ITS label — which the control is built to do —
           instead of pushing a control off the end of the row.
           Everything in this row is APP chrome and is set in the body face, not the `sign.font`
           the drawer is wearing — the sign is the band and the strip above, and the row below
           them is the app talking. -->
      <div
        style="
          flex-shrink: 0; margin-top: auto; display: flex; align-items: center;
          gap: {RAIL_GAP}px; padding: 4px {STRIP_PAD_X}px 0;
          font-family: var(--bjl-font-body);
        "
      >
        <div style="flex: 1; min-width: 0; display: flex; align-items: center; overflow: hidden;">
          <BoardedCrowdingReport
            vehicle={trackedVehicle}
            accentColor={routeColor}
            layout="row"
          />
        </div>
        <label
          for="ride-watch-3d"
          style="color: {Colors.textSecondary}; {Type.caption}; cursor: pointer; white-space: nowrap; font-family: var(--bjl-font-body);"
        >{RAIL_LABEL_3D}<span style={SR_ONLY}>{rail3dRest(isBus)}</span></label>
        <SwitchBare
          id="ride-watch-3d"
          size="sm"
          checked={watchingIn3d}
          onchange={(event) => setWatchIn3d(event.detail.checked)}
        />
      </div>
    {:else}
      <!-- The bus LCD over the farebox is one screen, not two, so this face keeps its
           headline stacked above the list. -->
      <div style="flex-shrink: 0; display: flex; align-items: center; gap: 12px; padding: 6px 20px 0;">
        <RouteBadge routeId={trackedVehicle.routeId} size={30} />
        <span
          bind:clientWidth={headlineW}
          style="
            flex: 1; min-width: 0; font-size: {headlinePx}px;
            letter-spacing: 0.02em; font-weight: 700;
            {sign.ink(sign.head, 10)}
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          "
        >
          {headlineText}
        </span>

        <!-- The ✕ and nothing else. The whole control cluster used to sit here and went DOWN
             to `BusStopScreen`'s footer, which is this face's control row; only the close
             button came back up, because this is the bus's ROUTE LINE — bullet, then where
             the bus is going — and that is the line the ✕ rides on every face.
             It costs the headline ~40px of the ~180 that move returned. `signHeadline` reads
             this span's own `clientWidth`, so the fitter simply sees a shorter row: ~2
             characters sooner to the first size step, and the cross street still drops before
             anything is clipped. -->
        {@render closeButton()}
      </div>

      <!-- Wrong vehicle warning -->
      {#if wrongVehicle}
        <div
          style="
            flex-shrink: 0; padding: 8px 20px 0; font-size: 15px;
            letter-spacing: 0.02em; font-weight: 700;
            {sign.ink(sign.urgent, 8)}
          "
        >
          THIS VEHICLE DOESN'T STOP AT YOUR DESTINATION; TAP A STOP BELOW
        </div>
      {:else if headline}
        <!-- Destination countdown -->
        <div
          style="
            flex-shrink: 0; padding: 8px 20px 0; font-size: 15px;
            letter-spacing: 0.02em; font-weight: 700;
            {sign.ink(atDestination || stopsUntilDestination === 1 ? sign.urgent : sign.calm, 8)}
          "
        >{headline}</div>
      {/if}

      <!-- The LCD, and with it this face's ONE control row — the screen owns the footer's
           geometry, this owns what goes in it. Crowding on the left, then the same pair the
           mini face carries and the train's control row repeats: the 3D toggle, on the right.
           Keep the three in step — a control added to one belongs in all of them, and
           `RAIL_LABEL_3D` / `rail3dRest` are shared so neither the wording nor the accessible
           name can drift. The ✕ that used to close this cluster is up on the route line — the
           headline row above — and belongs in none of the three.
           ⚠️ Same economies as the train's row, and for the same reason: the cluster's own
           gaps are `RAIL_GAP`, and crowding is the only flexible block (`flex: 1; min-width:
           0` in the screen's footer), so a narrow viewport shortens ITS label — which the
           control is built to do — instead of pushing the toggle off the end. -->
      <BusStopScreen
        stops={remaining}
        {destinationStopId}
        {isArriving}
        onPick={(stopId) => rider.value.setDestination(stopId)}
      >
        {#snippet footerLeft()}
          <BoardedCrowdingReport
            vehicle={trackedVehicle}
            accentColor={routeColor}
            layout="row"
          />
        {/snippet}
        {#snippet footerRight()}
          <div style="display: flex; align-items: center; gap: {RAIL_GAP}px;">
            <label
              for="ride-watch-3d-bus"
              style="color: {Colors.textSecondary}; {Type.caption}; cursor: pointer; white-space: nowrap; font-family: var(--bjl-font-body);"
            >{RAIL_LABEL_3D}<span style={SR_ONLY}>{rail3dRest(isBus)}</span></label>
            <SwitchBare
              id="ride-watch-3d-bus"
              size="sm"
              checked={watchingIn3d}
              onchange={(event) => setWatchIn3d(event.detail.checked)}
            />
          </div>
        {/snippet}
      </BusStopScreen>

      {@render extras()}
    {/if}
    </div>
  </div>
{/if}
